import type {
  BodyType,
  Currency,
  Fuel,
  Transmission,
  Vehicle,
  VehicleCondition,
} from '../types/vehicle'
import { imageUrl } from './drive'
import { WA_VENDEDOR } from '../data/vehicles'
import { SHEETS_ENDPOINT } from './sheets'

/**
 * Catálogo en vivo desde la hoja "Vehículos" de la planilla "Base de Datos VCARS".
 *
 * Se lee con el endpoint público gviz de Google Sheets, así que NO hace falta
 * tocar la planilla ni el Apps Script de los formularios. Requisito único:
 * la planilla debe estar compartida como "Cualquier persona con el enlace ·
 * Lector" (ya lo está).
 *
 * Las fotos pueden venir como link de Drive; se convierten a miniatura pública
 * en utils/drive.ts.
 */
export const SPREADSHEET_ID = '1zg4BJxd_A_7py4qV4gZYnfxhS1r-G7MRUG0re0GOgPk'
export const VEHICLES_SHEET = 'Vehículos'
export const DRIVE_FOLDER_URL =
  'https://drive.google.com/drive/folders/10a5G37Mer6sZHjSl_iNUkphlyjKhJ7zb'

const GVIZ_BASE = `https://docs.google.com/spreadsheets/d/${SPREADSHEET_ID}/gviz/tq?tqx=out:json`

/**
 * Se prueban varios nombres de hoja (con y sin tilde) y, como último recurso,
 * la primera hoja de la planilla. Así el catálogo funciona sin importar cómo
 * esté escrita la pestaña.
 */
const SHEET_CANDIDATES = ['Vehiculos', VEHICLES_SHEET, 'VEHICULOS', 'VEHÍCULOS']

type Cell = { v?: unknown; f?: string } | null
interface GvizResponse {
  table: {
    cols: { label?: string; id?: string }[]
    rows: { c: Cell[] }[]
  }
}

/** "Año Fabricación" → "anofabricacion" (sin acentos, sin espacios). */
function normalizeKey(label: string): string {
  return label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

type Row = Record<string, string>

function cellToString(cell: Cell): string {
  if (!cell) return ''
  if (cell.v === null || cell.v === undefined) return ''
  if (typeof cell.v === 'boolean') return cell.v ? 'Sí' : 'No'
  if (typeof cell.v === 'number') return String(cell.v)
  const raw = String(cell.v)
  // Fechas de gviz: Date(2026,7,18)
  const dateMatch = /^Date\((\d+),(\d+),(\d+)/.exec(raw)
  if (dateMatch) {
    const [, y, m, d] = dateMatch
    return `${y}-${String(Number(m) + 1).padStart(2, '0')}-${String(
      Number(d),
    ).padStart(2, '0')}`
  }
  return raw.trim()
}

function pick(row: Row, ...keys: string[]): string {
  for (const key of keys) {
    const value = row[normalizeKey(key)]
    if (value) return value
  }
  return ''
}

function toNumber(value: string): number {
  const cleaned = value.replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}\b)/g, '')
  const normalized = cleaned.replace(',', '.')
  const n = Number.parseFloat(normalized)
  return Number.isFinite(n) ? n : 0
}

function plain(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
}

/** Sí / Si / X / TRUE / 1 / disponible / activo… */
function toBool(value: string): boolean {
  const v = plain(value)
  return (
    v === 'si' ||
    v === 'true' ||
    v === 'x' ||
    v === '1' ||
    v === 'ok' ||
    v.startsWith('dispon') ||
    v.startsWith('activ') ||
    v.startsWith('public')
  )
}

const NEGATIVOS = ['no', 'false', '0', 'vendido', 'reservado', 'pausado', 'baja']

/**
 * La columna "Disponible" se interpreta con criterio amplio: la unidad se
 * publica salvo que diga explícitamente que no está disponible (No, Vendido,
 * Reservado…). Así admite "Sí", "disponible", "X" o incluso quedar vacía.
 */
function isDisponible(value: string): boolean {
  const v = plain(value)
  if (!v) return true
  if (NEGATIVOS.some((n) => v === n || v.startsWith(n))) return false
  return true
}

export function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

const BODY_TYPES: BodyType[] = ['Sedán', 'SUV', 'Pick-up', 'Hatchback', 'Coupé']
const FUELS: Fuel[] = ['Nafta', 'Diesel', 'Híbrido', 'GNC']

function matchOption<T extends string>(value: string, options: T[], fallback: T): T {
  const target = normalizeKey(value)
  const found = options.find((o) => normalizeKey(o) === target)
  if (found) return found
  const partial = options.find(
    (o) => target.includes(normalizeKey(o)) || normalizeKey(o).includes(target),
  )
  return partial ?? fallback
}

/** "peugeot" → "Peugeot", "buenos aires" → "Buenos Aires". Respeta siglas. */
function titleCase(value: string): string {
  if (!value) return ''
  if (value === value.toUpperCase() && value.length <= 4) return value
  return value
    .toLowerCase()
    .replace(/(^|[\s/-])([a-záéíóúñ])/g, (_, sep: string, char: string) =>
      sep + char.toUpperCase(),
    )
}

function rowToVehicle(row: Row): Vehicle | null {
  const marca = titleCase(pick(row, 'Marca'))
  const modelo = titleCase(pick(row, 'Modelo'))
  if (!marca && !modelo) return null

  const id = pick(row, 'ID', 'Id') || `${marca}-${modelo}`
  const version = titleCase(pick(row, 'Versión', 'Version'))
  const anio = toNumber(pick(row, 'Año', 'Anio', 'Ano'))
  const estadoRaw = pick(row, 'Estado')
  const estado: VehicleCondition = /0\s?km/i.test(estadoRaw) ? '0KM' : 'Usado'
  const monedaRaw = pick(row, 'Moneda').toUpperCase()
  const moneda: Currency = monedaRaw.includes('ARS') || monedaRaw.includes('$')
    ? monedaRaw.includes('USD')
      ? 'USD'
      : 'ARS'
    : 'USD'

  const galeriaRaw = [
    pick(row, 'Imagen principal', 'Imagen1', 'Imagen 1', 'Foto principal'),
    pick(row, 'Imagen 2', 'Imagen2', 'Foto 2'),
    pick(row, 'Imagen 3', 'Imagen3', 'Foto 3'),
    ...pick(row, 'Galería de imágenes', 'Galeria', 'Galería')
      .split(/[\n,;]+/)
      .map((s) => s.trim()),
  ].filter(Boolean)

  const galeria = Array.from(new Set(galeriaRaw)).map((u) => imageUrl(u))

  return {
    id,
    slug: slugify(`${marca}-${modelo}-${version}-${anio || ''}`) || slugify(id),
    marca,
    modelo,
    version,
    anio: anio || new Date().getFullYear(),
    kilometraje: toNumber(pick(row, 'Kilometraje', 'Km')),
    precio: toNumber(pick(row, 'Precio')),
    moneda,
    tipo: matchOption(pick(row, 'Tipo de vehículo', 'Tipo'), BODY_TYPES, 'Sedán'),
    estado,
    combustible: matchOption(pick(row, 'Combustible'), FUELS, 'Nafta'),
    transmision: /autom/i.test(pick(row, 'Transmisión', 'Transmision'))
      ? ('Automática' as Transmission)
      : ('Manual' as Transmission),
    color: titleCase(pick(row, 'Color')),
    ubicacion: titleCase(pick(row, 'Ubicación', 'Ubicacion')),
    descripcionCorta: pick(row, 'Descripción corta', 'Descripcion corta'),
    descripcionCompleta:
      pick(row, 'Descripción completa', 'Descripcion completa') ||
      pick(row, 'Descripción corta', 'Descripcion corta'),
    imagenPrincipal: galeria[0] ?? imageUrl(''),
    galeria: galeria.length > 0 ? galeria : [imageUrl('')],
    disponible: isDisponible(pick(row, 'Disponible')),
    destacado: toBool(pick(row, 'Destacado')),
    oportunidad: toBool(pick(row, 'Oportunidad')),
    financiacion: toBool(pick(row, 'Financiación disponible', 'Financiacion')),
    permuta: toBool(pick(row, 'Permuta aceptada', 'Permuta')),
    whatsapp:
      pick(row, 'WhatsApp de contacto', 'Whatsapp').replace(/\D/g, '') ||
      WA_VENDEDOR,
    vendedor: pick(row, 'Nombre del vendedor', 'Vendedor'),
    fechaPublicacion:
      pick(row, 'Fecha de publicación', 'Fecha') ||
      new Date().toISOString().slice(0, 10),
  }
}

async function readSheet(sheetName?: string): Promise<Vehicle[]> {
  const url = sheetName
    ? `${GVIZ_BASE}&sheet=${encodeURIComponent(sheetName)}&_=${Date.now()}`
    : `${GVIZ_BASE}&_=${Date.now()}`

  const response = await fetch(url)
  if (!response.ok) throw new Error(`HTTP ${response.status}`)

  const text = await response.text()
  const start = text.indexOf('(')
  const end = text.lastIndexOf(')')
  if (start === -1 || end === -1) throw new Error('Respuesta inesperada de Sheets')

  const data = JSON.parse(text.slice(start + 1, end)) as GvizResponse
  if (!data?.table?.cols) throw new Error('La hoja no devolvió columnas')

  const headers = data.table.cols.map((c) => normalizeKey(c.label || c.id || ''))
  // Si la hoja no trae encabezados propios, gviz devuelve A, B, C…: no sirve.
  if (!headers.includes('marca') && !headers.includes('modelo')) {
    throw new Error('La hoja no tiene las columnas Marca / Modelo')
  }

  return data.table.rows
    .map((r) => {
      const row: Row = {}
      headers.forEach((header, i) => {
        if (header) row[header] = cellToString(r.c?.[i] ?? null)
      })
      return rowToVehicle(row)
    })
    .filter((v): v is Vehicle => v !== null && v.disponible)
}

/**
 * Segunda vía: el Web App de Apps Script (el mismo que reciben los formularios).
 * Sirve cuando la planilla NO está compartida públicamente, porque el script
 * corre con la cuenta dueña de la planilla.
 * Requiere agregar el bloque `action=vehiculos` al doGet (ver docs).
 */
async function readFromAppsScript(): Promise<Vehicle[]> {
  const response = await fetch(
    `${SHEETS_ENDPOINT}?action=vehiculos&_=${Date.now()}`,
  )
  if (!response.ok) throw new Error(`HTTP ${response.status}`)

  const data = (await response.json()) as {
    ok?: boolean
    vehiculos?: Record<string, unknown>[]
    error?: string
  }
  if (!data.ok || !Array.isArray(data.vehiculos)) {
    throw new Error(data.error || 'El script no devolvió vehículos')
  }

  return data.vehiculos
    .map((raw) => {
      const row: Row = {}
      Object.entries(raw).forEach(([key, value]) => {
        row[normalizeKey(key)] =
          value === null || value === undefined ? '' : String(value).trim()
      })
      return rowToVehicle(row)
    })
    .filter((v): v is Vehicle => v !== null && v.disponible)
}

export interface CatalogResult {
  vehicles: Vehicle[]
  source: 'sheets' | 'script'
}

/** Lee la hoja "Vehículos" y devuelve solo las unidades con Disponible = Sí. */
export async function fetchVehiclesFromSheet(): Promise<CatalogResult> {
  let lastError: unknown = null

  for (const name of [...SHEET_CANDIDATES, undefined]) {
    try {
      const rows = await readSheet(name)
      if (rows.length > 0) return { vehicles: rows, source: 'sheets' }
      lastError = new Error(
        'La hoja respondió pero no hay filas con Disponible = Sí',
      )
    } catch (error) {
      lastError = error
    }
  }

  try {
    const rows = await readFromAppsScript()
    if (rows.length > 0) return { vehicles: rows, source: 'script' }
  } catch (error) {
    lastError = error
  }

  throw lastError ?? new Error('No se pudo leer el catálogo')
}
